
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';

const FREQUENCY_TABS = [
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' }
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface StepScheduleProps {
    frequency: string;
    setFrequency: (v: string) => void;
    weekdays: Record<string, boolean>;
    toggleWeekday: (day: string) => void;
    monthday: string;
    setMonthday: (v: string) => void;
    time: string;
    setTime: (v: string) => void;
    tz: string;
    setTz: (v: string) => void;
    nextRuns: string[];
}

export function StepSchedule({
    frequency,
    setFrequency,
    weekdays,
    toggleWeekday,
    monthday,
    setMonthday,
    time,
    setTime,
    tz,
    setTz,
    nextRuns
}: StepScheduleProps) {
    return (
        <div className="space-y-8">
            <div className="">
                <h2 className="mb-0 font-medium text-2xl">Schedule</h2>
                <p className="text-muted-foreground text-base">
                    Set when your agent runs and publishes analysis.
                </p>
            </div>

            <div className="flex items-center gap-6 space-y-1.5">
                <Label className="font-medium text-base">Frequency</Label>
                <Tabs value={frequency} onValueChange={setFrequency}>
                    <PillTabsList tabs={FREQUENCY_TABS} />
                </Tabs>
            </div>

            {frequency === 'Weekly' && (
                <div className="space-y-1.5">
                    <Label className="font-medium text-base">
                        On these days
                    </Label>
                    <div className="flex gap-2">
                        {WEEKDAYS.map((day) => (
                            <Button
                                key={day}
                                onClick={() => toggleWeekday(day)}
                                aria-pressed={weekdays[day]}
                                variant={weekdays[day] ? 'primary' : 'tertiary'}
                                size="icon"
                                className="rounded-[0.625rem] size-10 font-medium text-base"
                            >
                                {day[0]}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {frequency === 'Monthly' && (
                <div className="space-y-1.5">
                    <Label className="font-semibold text-base">
                        Day of month
                    </Label>
                    <Select value={monthday} onValueChange={setMonthday}>
                        <SelectTrigger className="rounded-lg">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {['1st', '15th', '28th', 'Last'].map((d) => (
                                <SelectItem key={d} value={d}>
                                    {d}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="gap-4 grid grid-cols-2">
                <div className="space-y-1.5">
                    <Label className="font-medium text-base">Time of day</Label>
                    <Select value={time} onValueChange={setTime}>
                        <SelectTrigger className="rounded-lg">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {[
                                '06:00',
                                '08:00',
                                '09:00',
                                '12:00',
                                '15:00',
                                '18:00'
                            ].map((t) => (
                                <SelectItem key={t} value={t}>
                                    {t}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="font-medium text-base">Timezone</Label>
                    <Select value={tz} onValueChange={setTz}>
                        <SelectTrigger className="rounded-lg">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {[
                                'Europe/Copenhagen',
                                'Europe/London',
                                'Europe/Berlin',
                                'UTC'
                            ].map((z) => (
                                <SelectItem key={z} value={z}>
                                    {z}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Next 3 runs */}
            <div className="bg-background dark:bg-muted/30 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                    {/* <span
                        className="inline-block rounded-full size-2 animate-pulse"
                        style={{ backgroundColor: '#16a34a' }}
                    /> */}
                    <span className="font-medium text-secondary-foreground text-base">
                        Next 3 runs
                    </span>
                </div>
                <div className="space-y-2">
                    {nextRuns.map((run, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                            <span className="bg-foreground rounded-full size-1.5 shrink-0" />
                            <span className="text-base">{run}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
